import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SnoozeIcon from "@mui/icons-material/Snooze";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import SummarizeIcon from "@mui/icons-material/Summarize";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import {
  ScheduledCall,
  TechnicianEntry,
  TechnicianTicket,
  haloTicketUrl,
  theoTicketUrl,
} from "../../apis/ticketLifecycleApi";
import { parseSummarySections } from "./insightFormat";
import theoLogo from "../../assets/img/theo_logo.svg";
import TicketNoteModal from "./TicketNoteModal";

/**
 * Color scale for "days since last update": green near the stale threshold
 * (~7 days) trending to red the longer a ticket has been untouched.
 */
const daysSinceColor = (days: number): string => {
  const min = 7;
  const max = 120;
  const t = Math.min(1, Math.max(0, (days - min) / (max - min)));
  const hue = 120 * (1 - t); // 120 = green → 0 = red
  return `hsl(${hue}, 65%, 38%)`;
};

const daysSince = (ts: string): number =>
  Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 86400000));

/**
 * Days-delay anchor: staleness is measured from the last *action* date
 * (Halo lastactiondate), not the last update. last_update_at is often "today"
 * (bookkeeping/status touches), so anchoring the delay on it would show "0 days"
 * for a ticket that's correctly flagged for attention off its older action date.
 * Falls back to last_update_at when last_action_at is null.
 */
const ticketDelayDays = (t: TechnicianTicket): number =>
  daysSince(t.last_action_at ?? t.last_update_at);

/**
 * Re-derive a technician entry from a subset of its tickets (used after
 * partitioning a technician's tickets into the overview vs upcoming-calls
 * sections). "N stale" and avg age are over the stale subset only.
 */
const subsetEntry = (entry: TechnicianEntry, tickets: TechnicianTicket[]): TechnicianEntry => {
  const stale = tickets.filter((t) => t.is_overdue);
  const ages = stale.map((t) => ticketDelayDays(t));
  const avg = ages.length ? ages.reduce((s, d) => s + d, 0) / ages.length : 0;
  return {
    ...entry,
    tickets,
    open_count: stale.length,
    avg_age_days: Math.round(avg * 10) / 10,
    upcoming_calls_count: tickets.reduce((s, t) => s + t.upcoming_calls.length, 0),
  };
};

/** Small "Collapse all / Expand all" toggle shown beside a section heading. */
export const CollapseToggle: React.FC<{ expanded: boolean; onToggle: () => void }> = ({ expanded, onToggle }) => (
  <Button
    size="small"
    color="inherit"
    startIcon={expanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
    onClick={onToggle}
    sx={{ color: "text.secondary", textTransform: "none", flexShrink: 0 }}
  >
    {expanded ? "Collapse all" : "Expand all"}
  </Button>
);

/**
 * Build a plain-text report of every ticket under a technician, in the form:
 *   # <ticket-id>
 *   <client>, <user> (N days old)
 *   Summary: <summary>
 *   Next Steps: <nextSteps>   // omitted when absent
 * Blocks separated by a blank line. client_name is stored as "Client / User";
 * rendered here comma-separated. The stored `summary` embeds both labelled
 * sections on one line, so we split it (parseSummarySections) and emit each on
 * its own line to match the dashboard. Summary/Next Steps lines are omitted when
 * their section is empty.
 */
export const buildTechnicianReport = (tickets: TechnicianTicket[]): string =>
  tickets
    .map((t) => {
      const days = ticketDelayDays(t);
      const who = String(t.client_name || "")
        .split(" / ")
        .map((s) => s.trim())
        .filter(Boolean)
        .join(", ");
      const lines = [`#${t.ticket_id}`, `${who} (${days} ${days === 1 ? "day" : "days"} old)`];
      const { summary, nextSteps } = parseSummarySections(t.summary);
      if (summary) lines.push(`Summary: ${summary}`);
      if (nextSteps) lines.push(`Next Steps: ${nextSteps}`);
      return lines.join("\n");
    })
    .join("\n\n");

const formatCallStart = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const formatCallDuration = (startAt: string, endAt: string | null): string | null => {
  if (!endAt) return null;
  const ms = new Date(endAt).getTime() - new Date(startAt).getTime();
  if (!(ms > 0)) return null;
  const mins = Math.round(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h} hr ${m} min`;
  if (h) return `${h} hr`;
  return `${m} min`;
};

/**
 * One labelled insight section: a bold title ("Summary" / "Next Steps") above its
 * body text. Rendered as inline spans (display:block) since it lives inside the
 * ListItemText `secondary` span. `disabled` greys it for the stale-error state.
 */
const InsightSection: React.FC<{ title: string; body: string; disabled?: boolean }> = ({
  title,
  body,
  disabled,
}) => (
  <Box component="span" sx={{ display: "block", mt: 0.5 }}>
    <Typography
      component="span"
      variant="caption"
      sx={{ display: "block", fontWeight: 700, color: disabled ? "text.disabled" : "text.primary" }}
    >
      {title}
    </Typography>
    <Typography
      component="span"
      variant="body2"
      sx={{ display: "block", color: disabled ? "text.disabled" : "text.primary" }}
    >
      {body}
    </Typography>
  </Box>
);

/**
 * Renders a stored insight `summary` string as its "Summary" and "Next Steps"
 * sections (each shown only when present). The single string is split here via
 * parseSummarySections, so callers just pass the raw `summary`.
 */
export const InsightSections: React.FC<{
  raw: string | null;
  disabled?: boolean;
}> = ({ raw, disabled }) => {
  const { summary, nextSteps } = parseSummarySections(raw);
  return (
    <>
      {summary ? <InsightSection title="Summary" body={summary} disabled={disabled} /> : null}
      {nextSteps ? <InsightSection title="Next Steps" body={nextSteps} disabled={disabled} /> : null}
    </>
  );
};

interface TicketRowProps {
  ticket: TechnicianTicket;
  onSnooze?: (ticketId: string | number) => Promise<void>;
  hideSnooze?: boolean;
  showCalls?: boolean;
  haloBaseUrl?: string;
}

const TicketRow: React.FC<TicketRowProps> = ({ ticket, onSnooze, hideSnooze, showCalls, haloBaseUrl }) => {
  const [snoozing, setSnoozing] = React.useState(false);
  const [noteOpen, setNoteOpen] = React.useState(false);

  const handleSnooze = async () => {
    if (!onSnooze) return;
    setSnoozing(true);
    try {
      await onSnooze(ticket.ticket_id);
    } finally {
      setSnoozing(false);
    }
  };

  const daysSinceUpdate = ticketDelayDays(ticket);
  const haloUrl = haloTicketUrl(haloBaseUrl ?? "", ticket.ticket_id);

  return (
    <ListItem alignItems="flex-start" divider>
      <ListItemText
        primary={
          <Typography variant="body2" fontWeight={600}>
            {haloUrl ? (
              <Link
                href={haloUrl}
                target="_blank"
                rel="noopener"
                underline="always"
                sx={{ color: "primary.main" }}
              >
                #{ticket.ticket_id}
              </Link>
            ) : (
              <>#{ticket.ticket_id}</>
            )}
            <Box component="span" sx={{ ml: 1.5 }}>
              {ticket.ticket_title}
            </Box>
          </Typography>
        }
        secondary={
          <Box component="span">
            <Typography component="span" variant="caption" color="text.secondary">
              {ticket.client_name}
            </Typography>
            {!showCalls && ticket.upcoming_calls.length > 0 && (
              <>
                {" · "}
                <Typography component="span" variant="caption" color="primary">
                  {ticket.upcoming_calls.length} upcoming call
                  {ticket.upcoming_calls.length !== 1 ? "s" : ""}
                </Typography>
              </>
            )}
            {ticket.summary_error ? (
              // Localized summary-generation error: slight red tint. A prior
              // summary/next-steps (if any) is still shown, greyed, beneath the note.
              <Box
                component="span"
                sx={{
                  display: "block",
                  mt: 0.5,
                  p: 0.75,
                  borderRadius: 1,
                  borderLeft: "3px solid",
                  borderColor: "error.main",
                  backgroundColor: "rgba(211,47,47,0.06)",
                }}
              >
                <InsightSections raw={ticket.summary} disabled />
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ display: "block", color: "error.main", fontWeight: 600 }}
                >
                  ⚠ {ticket.summary
                    ? "Couldn't refresh the summary — there was an issue generating the latest one."
                    : "There was an issue generating the summary."}
                </Typography>
              </Box>
            ) : ticket.summary ? (
              <InsightSections raw={ticket.summary} />
            ) : null}
            {showCalls && ticket.upcoming_calls.length > 0 && (
              <Box
                component="span"
                sx={{
                  display: "block",
                  mt: 1,
                  pt: 1,
                  borderTop: "1px dotted",
                  borderColor: "grey.400",
                }}
              >
                {ticket.upcoming_calls.map((call: ScheduledCall, i: number) => {
                  const duration = formatCallDuration(call.start_at, call.end_at);
                  return (
                    <Typography
                      key={i}
                      component="span"
                      variant="caption"
                      sx={{ display: "block", color: "text.primary", fontWeight: 700 }}
                    >
                      • {call.subject || "Scheduled call"} — {formatCallStart(call.start_at)}
                      {duration ? ` (${duration})` : ""}
                    </Typography>
                  );
                })}
              </Box>
            )}
          </Box>
        }
      />
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, ml: 2 }}>
        <Typography
          variant="caption"
          sx={{
            mr: 1,
            fontWeight: 600,
            whiteSpace: "nowrap",
            color: ticket.is_overdue ? daysSinceColor(daysSinceUpdate) : "text.secondary",
          }}
        >
          {daysSinceUpdate} {daysSinceUpdate === 1 ? "day" : "days"}
        </Typography>
        <Tooltip title="Open in Theo" arrow>
          <IconButton
            component={RouterLink}
            to={theoTicketUrl(ticket.ticket_id)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${ticket.ticket_title} in Theo`}
            size="small"
          >
            <Box component="img" src={theoLogo} alt="Theo" sx={{ width: 18, height: 18 }} />
          </IconButton>
        </Tooltip>
        {!hideSnooze && (
          <IconButton
            aria-label={`Snooze ${ticket.ticket_title}`}
            onClick={handleSnooze}
            disabled={snoozing}
            size="small"
          >
            {snoozing ? <CircularProgress size={16} /> : <SnoozeIcon fontSize="small" />}
          </IconButton>
        )}
        <Tooltip title="Add note to Halo" arrow>
          <IconButton
            aria-label={`Add note to ${ticket.ticket_title}`}
            onClick={() => setNoteOpen(true)}
            size="small"
          >
            <NoteAddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <TicketNoteModal
        open={noteOpen}
        ticketId={ticket.ticket_id}
        ticketTitle={ticket.ticket_title}
        haloBaseUrl={haloBaseUrl}
        prefillNote={ticket.notes}
        onClose={() => setNoteOpen(false)}
      />
    </ListItem>
  );
};

interface TechnicianSectionProps {
  entry: TechnicianEntry;
  onSnooze?: (ticketId: string | number) => Promise<void>;
  muted?: boolean;
  hideName?: boolean;
  defaultOpen?: boolean;
  callsMode?: boolean;
  allExpanded?: boolean;
  expandEpoch?: number;
  haloBaseUrl?: string;
}

const TechnicianSection: React.FC<TechnicianSectionProps> = ({
  entry,
  onSnooze,
  muted,
  hideName,
  defaultOpen = false,
  callsMode = false,
  allExpanded = true,
  expandEpoch = 0,
  haloBaseUrl,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);

  // Respond to the panel's "expand/collapse all" toggle. Driven off the epoch
  // (which bumps on every toggle click) so re-clicking re-applies even after a
  // section was toggled individually. Skips the initial mount (epoch 0) so each
  // section keeps its own defaultOpen until the user actually toggles all.
  React.useEffect(() => {
    if (expandEpoch > 0) setOpen(allExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandEpoch]);

  // "Generate report" — editable plain-text dump of this technician's tickets.
  const [reportOpen, setReportOpen] = React.useState(false);
  const [reportText, setReportText] = React.useState("");
  const [copied, setCopied] = React.useState(false);

  const handleGenerateReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    setReportText(buildTechnicianReport(entry.tickets));
    setCopied(false);
    setReportOpen(true);
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <Card sx={{ mb: 2, boxShadow: 1, ...(muted ? { bgcolor: "grey.50", opacity: 0.85 } : {}) }}>
      <CardContent>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, cursor: "pointer" }}
          onClick={() => setOpen((v) => !v)}
        >
          {!hideName && (
            <Typography variant="subtitle1" fontWeight={700}>
              {entry.agent_name}
            </Typography>
          )}
          {callsMode ? (
            <Chip
              color="primary"
              variant="outlined"
              size="small"
              label={`${entry.upcoming_calls_count} upcoming call${entry.upcoming_calls_count !== 1 ? "s" : ""}`}
            />
          ) : (
            <>
              <Chip label={`${entry.open_count} need attention`} size="small" />
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {entry.tickets.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SummarizeIcon />}
              onClick={handleGenerateReport}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              Briefing
            </Button>
          )}
          <IconButton size="small" aria-label={open ? "Collapse" : "Expand"}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Divider sx={{ mt: 1 }} />
          {entry.tickets.length > 0 ? (
            <List dense disablePadding>
              {entry.tickets.map((ticket) => (
                <TicketRow
                  key={String(ticket.ticket_id)}
                  ticket={ticket}
                  onSnooze={onSnooze}
                  hideSnooze={callsMode}
                  showCalls={callsMode}
                  haloBaseUrl={haloBaseUrl}
                />
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {callsMode ? "No upcoming calls." : "No tickets need attention."}
            </Typography>
          )}
        </Collapse>
      </CardContent>

      <Dialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: "70vh" } }}
      >
        <DialogTitle>Briefing — {entry.agent_name}</DialogTitle>
        <DialogContent dividers sx={{ overflow: "hidden" }}>
          <TextField
            value={reportText}
            onChange={(e) => setReportText(e.target.value)}
            multiline
            fullWidth
            minRows={10}
            variant="outlined"
            InputProps={{ sx: { fontFamily: "monospace", fontSize: 13, alignItems: "flex-start" } }}
            sx={{
              // Let the textarea grow with content but scroll *itself* once it
              // hits the cap (CSS max-height overrides the autosize inline height),
              // instead of the dialog growing / the container scrolling.
              "& .MuiInputBase-inputMultiline": {
                maxHeight: "calc(70vh - 180px)",
                overflowY: "auto !important",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)} color="inherit">
            Close
          </Button>
          <Button onClick={handleCopyAll} variant="contained" startIcon={<ContentCopyIcon />}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

interface TechnicianOverviewProps {
  data: TechnicianEntry[];
  onSnooze: (ticketId: string | number) => Promise<void>;
  haloBaseUrl?: string;
  // MSP-wide: Halo denied appointment access. Shows one notice in the calls area.
  appointmentsPermissionDenied?: boolean;
}

const TechnicianOverview: React.FC<TechnicianOverviewProps> = ({ data, onSnooze, haloBaseUrl, appointmentsPermissionDenied }) => {
  // Independent expand/collapse-all per section: the overview toggle controls
  // the technician (assigned + unassigned) cards; the calls toggle controls the
  // Upcoming Calls cards. Each epoch bumps on its own toggle so re-clicking
  // re-applies even after a card was toggled individually.
  const [overviewExpanded, setOverviewExpanded] = React.useState(true);
  const [overviewEpoch, setOverviewEpoch] = React.useState(0);
  const [callsExpanded, setCallsExpanded] = React.useState(true);
  const [callsEpoch, setCallsEpoch] = React.useState(0);
  const toggleOverview = () => {
    setOverviewExpanded((v) => !v);
    setOverviewEpoch((e) => e + 1);
  };
  const toggleCalls = () => {
    setCallsExpanded((v) => !v);
    setCallsEpoch((e) => e + 1);
  };
  // Partition each technician's tickets: any ticket with an upcoming appointment
  // moves to the dedicated Upcoming Calls section (even if it's also stale); the
  // rest (stale, no appointment) stay in the technician overview.
  const overviewData: TechnicianEntry[] = [];
  const callsData: TechnicianEntry[] = [];
  for (const entry of data) {
    const overviewTickets = entry.tickets.filter((t) => t.upcoming_calls.length === 0);
    const callTickets = entry.tickets.filter((t) => t.upcoming_calls.length > 0);
    if (overviewTickets.length) overviewData.push(subsetEntry(entry, overviewTickets));
    if (callTickets.length) callsData.push(subsetEntry(entry, callTickets));
  }

  // Assigned technicians first; the Unassigned group (agent_id === null) is
  // de-emphasized and rendered last as a muted card (like the snoozed section).
  const assigned = overviewData
    .filter((entry) => entry.agent_id !== null)
    .sort((a, b) => (a.agent_name ?? "").localeCompare(b.agent_name ?? ""));
  const unassigned = overviewData.filter((entry) => entry.agent_id === null);

  // Upcoming Calls keeps the same per-technician grouping but isn't muted —
  // these are the same priority as the overview. Assigned first, Unassigned last.
  const callsSorted = [
    ...callsData
      .filter((entry) => entry.agent_id !== null)
      .sort((a, b) => (a.agent_name ?? "").localeCompare(b.agent_name ?? "")),
    ...callsData.filter((entry) => entry.agent_id === null),
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Technician Overview
        </Typography>
        {overviewData.length > 0 && (
          <CollapseToggle expanded={overviewExpanded} onToggle={toggleOverview} />
        )}
      </Box>
      {overviewData.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No tickets need attention.
        </Typography>
      ) : (
        assigned.map((entry) => (
          <TechnicianSection
            key={entry.agent_id ?? "unassigned"}
            entry={entry}
            onSnooze={onSnooze}
            defaultOpen
            allExpanded={overviewExpanded}
            expandEpoch={overviewEpoch}
            haloBaseUrl={haloBaseUrl}
          />
        ))
      )}

      {/* Always rendered (like the Snoozed section): shows the permission notice,
          an empty state, or the per-technician call lists. */}
      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Upcoming Calls
          </Typography>
          {callsSorted.length > 0 && <CollapseToggle expanded={callsExpanded} onToggle={toggleCalls} />}
        </Box>
        {appointmentsPermissionDenied ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Theo doesn't have permission to read Halo appointments, so scheduled calls aren't shown.
            Grant appointment (calendar) access to Theo's Halo integration to enable this.
          </Alert>
        ) : callsSorted.length === 0 ? (
          <Card sx={{ boxShadow: 1, bgcolor: "grey.50", opacity: 0.85 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                No upcoming calls.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          callsSorted.map((entry) => (
            <TechnicianSection
              key={`calls-${entry.agent_id ?? "unassigned"}`}
              entry={entry}
              callsMode
              defaultOpen
              allExpanded={callsExpanded}
              expandEpoch={callsEpoch}
              haloBaseUrl={haloBaseUrl}
            />
          ))
        )}
      </Box>

      {unassigned.map((entry) => (
        <Box key="unassigned" sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" fontWeight={700} color="text.secondary" gutterBottom>
            Unassigned
          </Typography>
          <TechnicianSection
            entry={entry}
            onSnooze={onSnooze}
            muted
            hideName
            allExpanded={overviewExpanded}
            expandEpoch={overviewEpoch}
            haloBaseUrl={haloBaseUrl}
          />
        </Box>
      ))}
    </Box>
  );
};

export default TechnicianOverview;
